import React, { useState } from "react";
import { FaDollarSign } from "react-icons/fa";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Table,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

const AdMob = () => {
  const [values, setValues] = useState({
    impressions: "",
    adRequests: "",
    totalEarnings: "",
    activeUsers: "",
    sessions: "",
    adsPerSession: "",
    clicks: "",
    pageviews: "",
  });

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [goal, setGoal] = useState(""); // revenue goal input

  // handle input changes
  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  // formulas
  const impressions = parseFloat(values.impressions) || 0;
  const adRequests = parseFloat(values.adRequests) || 0;
  const totalEarnings = parseFloat(values.totalEarnings) || 0;
  const activeUsers = parseFloat(values.activeUsers) || 0;
  const sessions = parseFloat(values.sessions) || 0;
  const adsPerSession = parseFloat(values.adsPerSession) || 0;
  const clicks = parseFloat(values.clicks) || 0;

  const fillRate = adRequests ? (impressions / adRequests) * 100 : 0;
  const eCPM = impressions ? (totalEarnings / impressions) * 1000 : 0;
  const estimatedRevenue =
    activeUsers * sessions * adsPerSession * (eCPM / 1000);

  const ctr = impressions ? (clicks / impressions) * 100 : 0;
  const rpm = impressions ? (totalEarnings / impressions) * 1000 : 0;

  // Goal simulation
  const revenueGoal = parseFloat(goal) || 0;
  const impressionsNeeded = eCPM
    ? ((revenueGoal * 1000) / eCPM).toFixed(0)
    : 0;

  // Status message logic
  let status = "";
  let variant = "secondary";
  if (fillRate > 90) {
    status = "Your ads are running smoothly!";
    variant = "success";
  } else if (fillRate >= 60 && fillRate <= 90) {
    status = "Your ads are running moderately.";
    variant = "warning";
  } else {
    status = "Your ads are not running well.";
    variant = "danger";
  }

  // ...

  return (
    <Container className="py-5">
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center justify-center">
          <FaDollarSign className="mr-3 text-green-500" />
          AdMob Performance Dashboard
        </h2>

        {/* Input Form */}
        <Form className="mb-4">
          <Row className="mb-4">
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold text-secondary">Impressions</Form.Label>
                <Form.Control
                  type="number"
                  name="impressions"
                  value={values.impressions}
                  onChange={handleChange}
                  placeholder="Enter impressions"
                  style={{ fontSize: "0.9rem", padding: "8px 12px", height: "40px" }}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold text-secondary">Ad Requests</Form.Label>
                <Form.Control
                  type="number"
                  name="adRequests"
                  value={values.adRequests}
                  onChange={handleChange}
                  placeholder="Enter ad requests"
                  style={{ fontSize: "0.9rem", padding: "8px 12px", height: "40px" }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold text-secondary">Total Earnings ($)</Form.Label>
                <Form.Control
                  type="number"
                  name="totalEarnings"
                  value={values.totalEarnings}
                  onChange={handleChange}
                  placeholder="Enter earnings"
                  style={{ fontSize: "0.9rem", padding: "8px 12px", height: "40px" }}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold text-secondary">Active Users</Form.Label>
                <Form.Control
                  type="number"
                  name="activeUsers"
                  value={values.activeUsers}
                  onChange={handleChange}
                  placeholder="Enter active users"
                  style={{ fontSize: "0.9rem", padding: "8px 12px", height: "40px" }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={4} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold text-secondary">Sessions</Form.Label>
                <Form.Control
                  type="number"
                  name="sessions"
                  value={values.sessions}
                  onChange={handleChange}
                  placeholder="Enter sessions"
                  style={{ fontSize: "0.9rem", padding: "8px 12px", height: "40px" }}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold text-secondary">Ads per Session</Form.Label>
                <Form.Control
                  type="number"
                  name="adsPerSession"
                  value={values.adsPerSession}
                  onChange={handleChange}
                  placeholder="Enter ads/session"
                  style={{ fontSize: "0.9rem", padding: "8px 12px", height: "40px" }}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold text-secondary">Clicks</Form.Label>
                <Form.Control
                  type="number"
                  name="clicks"
                  value={values.clicks}
                  onChange={handleChange}
                  placeholder="Enter clicks"
                  style={{ fontSize: "0.9rem", padding: "8px 12px", height: "40px" }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>

        {/* Status */}
        <Alert variant={variant} className="mt-4 mb-4">
          {status}
          {fillRate < 90 && (
            <div className="mt-3">
              <Button
                variant={
                  variant === "warning" ? "outline-warning" : "outline-danger"
                }
                size="sm"
                onClick={() => setShowSuggestions(!showSuggestions)}
              >
                {showSuggestions ? "Hide Suggestions" : "Get Suggestions"}
              </Button>
            </div>
          )}
        </Alert>

        {/* Suggestions */}
        {showSuggestions && (
          <Table striped bordered hover className="mt-4">
            <thead>
              <tr>
                <th>Area</th>
                <th>Suggestion</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Fill Rate</td>
                <td>Ensure proper ad placement and mediation setup.</td>
              </tr>
              <tr>
                <td>eCPM</td>
                <td>Try rewarded ads or experiment with interstitial formats.</td>
              </tr>
              <tr>
                <td>Revenue</td>
                <td>Increase user sessions & retention for higher revenue.</td>
              </tr>
              <tr>
                <td>CTR</td>
                <td>Make ads more visible but non-intrusive for better clicks.</td>
              </tr>
            </tbody>
          </Table>
        )}

        {/* Calculated Results */}
        <div className="mt-5 p-4 bg-light rounded">
          <h5 className="mb-4">Calculated Metrics</h5>

          {/* Fill Rate with tooltip + progress bar */}
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                Percentage of ad requests that filled with impressions.
              </Tooltip>
            }
          >
            <div className="mb-4">
              <b>Fill Rate:</b> {fillRate.toFixed(2)}%
              <ProgressBar
                now={fillRate}
                variant={variant}
                label={`${fillRate.toFixed(1)}%`}
                className="mt-2"
              />
              <small>
                Benchmark: <span className="text-success">&gt;90% Good</span>,{" "}
                <span className="text-warning">60–90% Average</span>,{" "}
                <span className="text-danger">&lt;60% Poor</span>
              </small>
            </div>
          </OverlayTrigger>

          {/* eCPM */}
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Effective cost per 1000 impressions.</Tooltip>}
          >
            <div className="mb-4">
              <b>eCPM:</b> ${eCPM.toFixed(2)}
              <ProgressBar
                now={Math.min(eCPM, 20)}
                variant="info"
                label={`$${eCPM.toFixed(2)}`}
                className="mt-2"
              />
              <small>Typical range: $1 – $5 (varies by region & app)</small>
            </div>
          </OverlayTrigger>

          {/* CTR */}
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>Click-through rate (clicks ÷ impressions).</Tooltip>
            }
          >
            <div className="mb-4">
              <b>CTR:</b> {ctr.toFixed(2)}%
              <ProgressBar
                now={ctr}
                variant="primary"
                label={`${ctr.toFixed(1)}%`}
                className="mt-2"
              />
              <small>Benchmark: ~1–2% is common</small>
            </div>
          </OverlayTrigger>

          {/* Other Metrics */}
          <ul className="mt-3">
            <li className="mb-2">
              <b>Estimated Revenue:</b> ${estimatedRevenue.toFixed(2)}
            </li>
            <li>
              <b>RPM:</b> ${rpm.toFixed(2)}
            </li>
          </ul>
        </div>

        {/* Revenue Goal Simulator */}
        <div className="mt-5 p-4 bg-light rounded">
          <h5 className="mb-4">Revenue Goal Simulator</h5>
          <Form>
            <Form.Group>
              <Form.Label>Enter Your Daily Revenue Goal ($)</Form.Label>
              <Form.Control
                type="number"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="e.g., 100"
              />
            </Form.Group>
          </Form>
          {goal && (
            <Alert variant="info" className="mt-4">
              To reach <b>${goal}</b> at current eCPM, you need approximately{" "}
              <b>{impressionsNeeded}</b> impressions.
            </Alert>
          )}
        </div>
      </div>
    </Container>
  );
};

export default AdMob;
